import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import {
  fetchAppConfig,
  fetchMyCreatorProfile,
  fetchRecommendedFees,
  fetchWalletAccount,
  linkWalletAccount,
  removeWalletAccount,
  requestLaunchpadChallenge,
  requestLaunchpadSession,
  setMainWalletAccount,
} from "./lib/api";
import type { WalletAccount } from "./lib/account";
import type { CreatorProfile } from "./lib/launchpad";
import type { AppConfig, ConnectedWallet, RecommendedFees, SignedPackage, TransactionSummary, WalletProviderId } from "./lib/types";
import { assertWalletSelected, connectWalletAddresses, signWalletOwnershipMessage, subscribeWalletChanges, type WalletConnectionCandidate } from "./lib/wallets";

import { sessionMatchesWallet, type StoredWalletSession as StoredSession } from "./lib/wallet-session";

interface CompletionRecord {
  package: SignedPackage;
  summary: TransactionSummary;
  name: string;
  count: number;
  broadcast: boolean;
  returnPath?: string;
}

type WalletModalMode = "connect" | "link";

export interface AppState {
  config: AppConfig | null;
  fees: RecommendedFees | null;
  wallet: ConnectedWallet | null;
  linkedWallets: ConnectedWallet[];
  account: WalletAccount | null;
  setWallet: (wallet: ConnectedWallet | null) => void;
  connectPrimaryWallet: (provider: WalletProviderId) => Promise<void>;
  switchWallet: (paymentAddress: string) => boolean;
  reconnectWallet: (paymentAddress: string) => Promise<void>;
  linkWallet: (provider: WalletProviderId) => Promise<void>;
  removeLinkedWallet: (paymentAddress: string) => Promise<void>;
  makeMainWallet: (paymentAddress: string) => Promise<void>;
  accountBusy: boolean;
  accountError: string;
  clearAccountError: () => void;
  authorization: string | null;
  ordinalsVerified: boolean;
  authorizeWallet: (verifyOrdinals?: boolean) => Promise<string | null>;
  disconnectWallet: () => void;
  creatorProfile: CreatorProfile | null;
  setCreatorProfile: (profile: CreatorProfile | null) => void;
  profileLoading: boolean;
  walletModalOpen: boolean;
  setWalletModalOpen: (open: boolean) => void;
  walletModalMode: WalletModalMode;
  setWalletModalMode: (mode: WalletModalMode) => void;
  completion: CompletionRecord | null;
  setCompletion: (record: CompletionRecord | null) => void;
  loading: boolean;
  error: string;
}

const AppStateContext = createContext<AppState | null>(null);
export function ControlledAppProvider({ value, children }: PropsWithChildren<{ value: AppState }>) {
  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}
const WALLET_STORAGE_KEY = "stc-connected-wallet";
const LINKED_WALLETS_STORAGE_KEY = "stc-linked-wallets";
const SESSION_STORAGE_KEY = "stc-launchpad-session";
const SESSIONS_STORAGE_KEY = "stc-wallet-sessions";
const ACCOUNT_STORAGE_KEY = "stc-wallet-account";
const PROFILE_STORAGE_PREFIX = "stc-creator-profile:";

function validConnectedWallet(value: unknown): value is ConnectedWallet {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<ConnectedWallet>;
  return (candidate.provider === "xverse" || candidate.provider === "unisat")
    && candidate.ownershipVerified === true
    && typeof candidate.payment?.address === "string"
    && typeof candidate.payment.publicKey === "string"
    && candidate.payment.purpose === "payment"
    && typeof candidate.ordinals?.address === "string"
    && typeof candidate.ordinals.publicKey === "string"
    && candidate.ordinals.purpose === "ordinals"
    && candidate.ordinals.address.startsWith("bc1p");
}

function readStoredWallet(): ConnectedWallet | null {
  try {
    const value: unknown = JSON.parse(window.localStorage.getItem(WALLET_STORAGE_KEY) ?? "null");
    return validConnectedWallet(value) ? value : null;
  } catch {
    return null;
  }
}

function readStoredLinkedWallets(active: ConnectedWallet | null): ConnectedWallet[] {
  try {
    const value: unknown = JSON.parse(window.localStorage.getItem(LINKED_WALLETS_STORAGE_KEY) ?? "[]");
    const wallets = Array.isArray(value) ? value.filter(validConnectedWallet) : [];
    if (active && !wallets.some((item) => item.payment.address.toLowerCase() === active.payment.address.toLowerCase())) wallets.unshift(active);
    return wallets;
  } catch {
    return active ? [active] : [];
  }
}

function persistLinkedWallets(wallets: ConnectedWallet[]): void {
  window.localStorage.setItem(LINKED_WALLETS_STORAGE_KEY, JSON.stringify(wallets));
}

function validStoredSession(value: unknown): value is StoredSession {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as { version?: number; authorization?: unknown; expiresAt?: unknown; paymentAddress?: unknown };
  return (candidate.version === 2 || candidate.version === 3)
    && typeof candidate.authorization === "string"
    && typeof candidate.expiresAt === "number"
    && candidate.expiresAt > Date.now()
    && typeof candidate.paymentAddress === "string";
}

function readStoredSessions(): Record<string, StoredSession> {
  const sessions: Record<string, StoredSession> = {};
  try {
    const value: unknown = JSON.parse(window.localStorage.getItem(SESSIONS_STORAGE_KEY) ?? "{}");
    if (typeof value === "object" && value !== null) {
      for (const candidate of Object.values(value)) {
        if (!validStoredSession(candidate)) continue;
        sessions[candidate.paymentAddress.toLowerCase()] = { ...candidate, version: 3 };
      }
    }
    const legacy: unknown = JSON.parse(window.localStorage.getItem(SESSION_STORAGE_KEY) ?? window.sessionStorage.getItem(SESSION_STORAGE_KEY) ?? "null");
    if (validStoredSession(legacy)) sessions[legacy.paymentAddress.toLowerCase()] = { ...legacy, version: 3 };
  } catch {
    // Invalid stored sessions are discarded below.
  }
  window.localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(sessions));
  window.localStorage.removeItem(SESSION_STORAGE_KEY);
  window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  return sessions;
}

function persistSessions(sessions: Record<string, StoredSession>): void {
  window.localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(sessions));
}

function readStoredAccount(active: ConnectedWallet | null): WalletAccount | null {
  try {
    const value = JSON.parse(window.localStorage.getItem(ACCOUNT_STORAGE_KEY) ?? "null") as Partial<WalletAccount> | null;
    if (!value || typeof value.accountId !== "string" || !Array.isArray(value.wallets)) return null;
    if (active && !value.wallets.some((item) => item.paymentAddress.toLowerCase() === active.payment.address.toLowerCase())) return null;
    return value as WalletAccount;
  } catch {
    return null;
  }
}

function profileStorageKey(paymentAddress: string): string {
  return `${PROFILE_STORAGE_PREFIX}${paymentAddress.toLowerCase()}`;
}

function readStoredProfile(paymentAddress: string): CreatorProfile | null {
  try {
    const candidate = JSON.parse(window.localStorage.getItem(profileStorageKey(paymentAddress)) ?? "null") as Partial<CreatorProfile> | null;
    return candidate && typeof candidate.username === "string" && typeof candidate.displayName === "string" ? candidate as CreatorProfile : null;
  } catch {
    return null;
  }
}

const fallbackFees: RecommendedFees = {
  fastestFee: 18,
  halfHourFee: 12,
  hourFee: 8,
  economyFee: 5,
  minimumFee: 1,
};

export function AppProvider({ children }: PropsWithChildren) {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [fees, setFees] = useState<RecommendedFees | null>(null);
  const [wallet, setWalletState] = useState<ConnectedWallet | null>(readStoredWallet);
  const [linkedWallets, setLinkedWallets] = useState<ConnectedWallet[]>(() => readStoredLinkedWallets(wallet));
  const [sessions, setSessions] = useState<Record<string, StoredSession>>(readStoredSessions);
  const [account, setAccountState] = useState<WalletAccount | null>(() => readStoredAccount(wallet));
  const [creatorProfile, setCreatorProfileState] = useState<CreatorProfile | null>(() => wallet ? readStoredProfile(wallet.payment.address) : null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [accountBusy, setAccountBusy] = useState(false);
  const [accountError, setAccountError] = useState("");
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [walletModalMode, setWalletModalMode] = useState<WalletModalMode>("connect");
  const [completion, setCompletion] = useState<CompletionRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const authorizationRequests = useRef(new Map<string, Promise<StoredSession>>());

  const accountRevision = useRef(0);
  const identityRevision = useRef(0);
  const providerRevision = useRef(0);

  const storeAccount = useCallback((next: WalletAccount | null) => {
    setAccountState(next);
    if (next) window.localStorage.setItem(ACCOUNT_STORAGE_KEY, JSON.stringify(next));
    else window.localStorage.removeItem(ACCOUNT_STORAGE_KEY);
  }, []);

  const storeProfile = useCallback((profile: CreatorProfile | null, nextAccount: WalletAccount | null, activeWallet: ConnectedWallet | null) => {
    setCreatorProfileState(profile);
    const addresses = nextAccount?.wallets.map((item) => item.paymentAddress) ?? (activeWallet ? [activeWallet.payment.address] : []);
    for (const address of addresses) {
      if (profile) window.localStorage.setItem(profileStorageKey(address), JSON.stringify(profile));
      else window.localStorage.removeItem(profileStorageKey(address));
    }
  }, []);

  const setCreatorProfile = useCallback((profile: CreatorProfile | null) => storeProfile(profile, account, wallet), [account, storeProfile, wallet]);

  const setWallet = useCallback((nextWallet: ConnectedWallet | null) => {
    identityRevision.current += 1;
    accountRevision.current += 1;
    if (!nextWallet) {
      setWalletState(null);
      setLinkedWallets([]);
      setSessions({});
      storeAccount(null);
      setCreatorProfileState(null);
      authorizationRequests.current.clear();
      window.localStorage.removeItem(WALLET_STORAGE_KEY);
      window.localStorage.removeItem(LINKED_WALLETS_STORAGE_KEY);
      window.localStorage.removeItem(SESSIONS_STORAGE_KEY);
      window.localStorage.removeItem(SESSION_STORAGE_KEY);
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return;
    }
    const belongsToCurrentAccount = account?.wallets.some((item) => item.paymentAddress.toLowerCase() === nextWallet.payment.address.toLowerCase()) ?? false;
    const nextLinked = belongsToCurrentAccount
      ? [...linkedWallets.filter((item) => item.payment.address.toLowerCase() !== nextWallet.payment.address.toLowerCase()), nextWallet]
      : [nextWallet];
    setWalletState(nextWallet);
    setLinkedWallets(nextLinked);
    persistLinkedWallets(nextLinked);
    window.localStorage.setItem(WALLET_STORAGE_KEY, JSON.stringify(nextWallet));
    if (!belongsToCurrentAccount) {
      setSessions({});
      persistSessions({});
      authorizationRequests.current.clear();
      storeAccount(null);
      setCreatorProfileState(readStoredProfile(nextWallet.payment.address));
    }
  }, [account, linkedWallets, storeAccount]);

  useEffect(() => {
    const provider = wallet?.provider;
    const previousPaymentAddress = wallet?.payment.address.toLowerCase();
    if (!provider || !previousPaymentAddress) return;
    let active = true;
    const unsubscribe = subscribeWalletChanges(provider, () => {
      providerRevision.current += 1;
      authorizationRequests.current.clear();
      // A provider event invalidates the previous wallet immediately. If the
      // newly selected wallet already has an exact cached session it can be
      // activated seamlessly; otherwise the next protected action asks it to
      // verify without forcing a full STC logout.
      setSessions((current) => {
        const next = { ...current };
        delete next[previousPaymentAddress];
        persistSessions(next);
        return next;
      });
      void connectWalletAddresses(provider, config?.appName ?? "Spam The Chain")
        .then((candidate) => {
          if (!active) return;
          const known = linkedWallets.find((item) => sessionMatchesWallet(sessions[item.payment.address.toLowerCase()], candidate));
          // The provider has returned the selected addresses, but protected
          // APIs still require their exact signed STC session below.
          setWallet({ ...candidate, ownershipVerified: true });
          setAccountError(known ? "" : "Wallet changed. Verify the selected wallet to continue.");
        })
        .catch(() => {
          if (active) setAccountError("Wallet changed. Select a Bitcoin mainnet wallet and reconnect.");
        });
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [config?.appName, linkedWallets, sessions, setWallet, wallet?.payment.address, wallet?.provider]);

  const switchWallet = useCallback((paymentAddress: string): boolean => {
    const next = linkedWallets.find((item) => item.payment.address.toLowerCase() === paymentAddress.toLowerCase());
    if (!next) {
      setAccountError("Reconnect this wallet before using it on this device");
      return false;
    }
    setAccountError("");
    identityRevision.current += 1;
    accountRevision.current += 1;
    setWalletState(next);
    window.localStorage.setItem(WALLET_STORAGE_KEY, JSON.stringify(next));
    return true;
  }, [linkedWallets]);

  const establishWalletSession = useCallback(async (target: ConnectedWallet | WalletConnectionCandidate, verifyOrdinals = false, fresh = false): Promise<StoredSession> => {
    const key = target.payment.address.toLowerCase();
    const existing = sessions[key];
    if (!fresh && sessionMatchesWallet(existing, target, verifyOrdinals)) return existing;
    const identity = identityRevision.current;
    const selection = providerRevision.current;
    const assertCurrent = () => {
      if (identity !== identityRevision.current || selection !== providerRevision.current) throw new Error("Wallet changed during verification; try again");
    };
    const requestKey = `${key}:${target.ordinals.address}:${target.payment.publicKey}:${target.ordinals.publicKey}:${target.provider}:${verifyOrdinals}:${fresh}:${identity}:${selection}`;
    const pending = authorizationRequests.current.get(requestKey);
    if (pending) return pending;
    const request = (async () => {
      await assertWalletSelected(target);
      assertCurrent();
      const challenge = await requestLaunchpadChallenge(target.payment.address, target.ordinals.address, target.payment.publicKey, target.ordinals.publicKey, target.provider);
      assertCurrent();
      const signature = await signWalletOwnershipMessage(target, target.payment.address, challenge.message);
      assertCurrent();
      if (verifyOrdinals) {
        await assertWalletSelected(target);
        assertCurrent();
      }
      const ordinalsSignature = verifyOrdinals
        ? await signWalletOwnershipMessage(target, target.ordinals.address, challenge.message) : undefined;
      assertCurrent();
      const created = await requestLaunchpadSession(challenge.challengeToken, signature, ordinalsSignature);
      assertCurrent();
      await assertWalletSelected(target);
      assertCurrent();
      if (verifyOrdinals && created.ordinalsVerified !== true) throw new Error("Taproot wallet ownership could not be verified");
      const stored: StoredSession = { ...created, version: 3, paymentAddress: target.payment.address, ordinalsAddress: target.ordinals.address, paymentPublicKey: target.payment.publicKey, ordinalsPublicKey: target.ordinals.publicKey, provider: target.provider };
      setSessions((current) => {
        if (identity !== identityRevision.current || selection !== providerRevision.current) return current;
        const next = { ...current, [key]: stored };
        persistSessions(next);
        return next;
      });
      return stored;
    })().finally(() => authorizationRequests.current.delete(requestKey));
    authorizationRequests.current.set(requestKey, request);
    return request;
  }, [sessions]);

  const activeSession = wallet ? sessions[wallet.payment.address.toLowerCase()] : undefined;
  const authorization = wallet && sessionMatchesWallet(activeSession, wallet) ? activeSession.authorization : null;
  const ordinalsVerified = Boolean(wallet && sessionMatchesWallet(activeSession, wallet, true));

  const authorizeWallet = useCallback(async (verifyOrdinals = false): Promise<string | null> => {
    if (!wallet) return null;
    return (await establishWalletSession(wallet, verifyOrdinals)).authorization;
  }, [establishWalletSession, wallet]);

  const connectPrimaryWallet = useCallback(async (provider: WalletProviderId): Promise<void> => {
    const revision = ++accountRevision.current;
    const assertAccountCurrent = () => {
      if (revision !== accountRevision.current) throw new Error("Account changed during this operation; try again");
    };
    setAccountBusy(true);
    setAccountError("");
    try {
      const candidate = await connectWalletAddresses(provider, config?.appName ?? "Spam The Chain");
      assertAccountCurrent();
      await establishWalletSession(candidate, false, true);
      assertAccountCurrent();
      const connected: ConnectedWallet = { ...candidate, ownershipVerified: true };
      setWalletState(connected);
      setLinkedWallets([connected]);
      persistLinkedWallets([connected]);
      window.localStorage.setItem(WALLET_STORAGE_KEY, JSON.stringify(connected));
      storeAccount(null);
      setCreatorProfileState(readStoredProfile(connected.payment.address));
    } catch (caught) {
      setAccountError(caught instanceof Error ? caught.message : "Wallet connection failed");
      throw caught;
    } finally {
      setAccountBusy(false);
    }
  }, [config?.appName, establishWalletSession, storeAccount]);

  const linkWallet = useCallback(async (provider: WalletProviderId): Promise<void> => {
    if (!wallet) throw new Error("Connect your main wallet first");
    const revision = ++accountRevision.current;
    const assertAccountCurrent = () => {
      if (revision !== accountRevision.current) throw new Error("Account changed during this operation; try again");
    };
    setAccountBusy(true);
    setAccountError("");
    try {
      // Never ask the previous provider account to sign after the user has switched accounts.
      const ownerWallet = [wallet, ...linkedWallets].find(item =>
        (!account || account.wallets.some(record => record.paymentAddress === item.payment.address))
        && sessionMatchesWallet(sessions[item.payment.address.toLowerCase()], item));
      const owner = ownerWallet && sessions[ownerWallet.payment.address.toLowerCase()];
      if (!owner) throw new Error("Your account session expired. Sign in with a linked wallet before switching accounts to add another wallet.");
      const candidate = await connectWalletAddresses(provider, config?.appName ?? "Spam The Chain");
      if (candidate.payment.address.toLowerCase() === wallet.payment.address.toLowerCase()) throw new Error("Choose a different wallet account before adding it");
      assertAccountCurrent();
      const linkedSession = await establishWalletSession(candidate, false, true);
      assertAccountCurrent();
      const linked: ConnectedWallet = { ...candidate, ownershipVerified: true };
      const nextAccount = await linkWalletAccount(owner.authorization, linkedSession.authorization);
      assertAccountCurrent();
      const nextLinked = [...linkedWallets.filter((item) => item.payment.address.toLowerCase() !== linked.payment.address.toLowerCase()), linked];
      setLinkedWallets(nextLinked);
      persistLinkedWallets(nextLinked);
      storeAccount(nextAccount);
      storeProfile(creatorProfile, nextAccount, linked);
      setWalletState(linked);
      window.localStorage.setItem(WALLET_STORAGE_KEY, JSON.stringify(linked));
      setWalletModalOpen(false);
      setWalletModalMode("connect");
    } catch (caught) {
      setAccountError(caught instanceof Error ? caught.message : "Wallet could not be linked");
      throw caught;
    } finally {
      setAccountBusy(false);
    }
  }, [account, config?.appName, creatorProfile, establishWalletSession, linkedWallets, sessions, storeAccount, storeProfile, wallet]);

  const reconnectWallet = useCallback(async (paymentAddress: string): Promise<void> => {
    const record = account?.wallets.find((item) => item.paymentAddress.toLowerCase() === paymentAddress.toLowerCase());
    if (!record?.provider) throw new Error("Choose Add wallet and reconnect this address first");
    const revision = ++accountRevision.current;
    const assertAccountCurrent = () => {
      if (revision !== accountRevision.current) throw new Error("Account changed during this operation; try again");
    };
    setAccountBusy(true);
    setAccountError("");
    try {
      const candidate = await connectWalletAddresses(record.provider, config?.appName ?? "Spam The Chain");
      if (candidate.payment.address.toLowerCase() !== record.paymentAddress.toLowerCase() || candidate.ordinals.address.toLowerCase() !== record.ordinalsAddress.toLowerCase()) {
        throw new Error(`Select ${record.paymentAddress} in ${record.provider === "xverse" ? "Xverse" : "UniSat"} and try again`);
      }
      assertAccountCurrent();
      await establishWalletSession(candidate, false, true);
      assertAccountCurrent();
      const connected: ConnectedWallet = { ...candidate, ownershipVerified: true };
      const nextLinked = [...linkedWallets.filter((item) => item.payment.address.toLowerCase() !== connected.payment.address.toLowerCase()), connected];
      setLinkedWallets(nextLinked);
      persistLinkedWallets(nextLinked);
      setWalletState(connected);
      window.localStorage.setItem(WALLET_STORAGE_KEY, JSON.stringify(connected));
    } catch (caught) {
      setAccountError(caught instanceof Error ? caught.message : "Wallet could not be reconnected");
      throw caught;
    } finally {
      setAccountBusy(false);
    }
  }, [account?.wallets, config?.appName, establishWalletSession, linkedWallets]);

  const removeLinkedWallet = useCallback(async (paymentAddress: string): Promise<void> => {
    const revision = ++accountRevision.current;
    const assertAccountCurrent = () => {
      if (revision !== accountRevision.current) throw new Error("Account changed during this operation; try again");
    };
    setAccountBusy(true);
    setAccountError("");
    try {
      const token = await authorizeWallet();
      if (!token) throw new Error("Wallet verification required");
      assertAccountCurrent();
      const nextAccount = await removeWalletAccount(token, paymentAddress);
      assertAccountCurrent();
      const nextLinked = linkedWallets.filter((item) => item.payment.address.toLowerCase() !== paymentAddress.toLowerCase());
      setLinkedWallets(nextLinked);
      persistLinkedWallets(nextLinked);
      setSessions((current) => {
        const next = { ...current };
        delete next[paymentAddress.toLowerCase()];
        persistSessions(next);
        return next;
      });
      storeAccount(nextAccount);
      if (wallet?.payment.address.toLowerCase() === paymentAddress.toLowerCase()) {
        const primary = nextAccount.wallets.find((item) => item.isPrimary);
        const localPrimary = (primary && nextLinked.find((item) => item.payment.address.toLowerCase() === primary.paymentAddress.toLowerCase()))
          || nextLinked.find(item => nextAccount.wallets.some(record => record.paymentAddress === item.payment.address));
        if (localPrimary) {
          setWalletState(localPrimary);
          window.localStorage.setItem(WALLET_STORAGE_KEY, JSON.stringify(localPrimary));
        } else {
          setWalletState(null);
          window.localStorage.removeItem(WALLET_STORAGE_KEY);
          setAccountError("Wallet removed. Reconnect a remaining linked wallet to continue.");
        }
      }
    } catch (caught) {
      setAccountError(caught instanceof Error ? caught.message : "Wallet could not be removed");
      throw caught;
    } finally {
      setAccountBusy(false);
    }
  }, [authorizeWallet, linkedWallets, storeAccount, wallet?.payment.address]);

  const makeMainWallet = useCallback(async (paymentAddress: string): Promise<void> => {
    const revision = ++accountRevision.current;
    const assertAccountCurrent = () => {
      if (revision !== accountRevision.current) throw new Error("Account changed during this operation; try again");
    };
    setAccountBusy(true);
    setAccountError("");
    try {
      const token = await authorizeWallet();
      if (!token) throw new Error("Wallet verification required");
      assertAccountCurrent();
      const nextAccount = await setMainWalletAccount(token, paymentAddress);
      assertAccountCurrent();
      storeAccount(nextAccount);
      storeProfile(creatorProfile, nextAccount, wallet);
    } catch (caught) {
      setAccountError(caught instanceof Error ? caught.message : "Main wallet could not be changed");
      throw caught;
    } finally {
      setAccountBusy(false);
    }
  }, [authorizeWallet, creatorProfile, storeAccount, storeProfile, wallet]);

  const disconnectWallet = useCallback(() => {
    setWallet(null);
    setWalletModalOpen(false);
    setWalletModalMode("connect");
    setAccountError("");
  }, [setWallet]);

  useEffect(() => {
    if (!wallet) {
      setCreatorProfileState(null);
      setProfileLoading(false);
      return;
    }
    const cached = readStoredProfile(wallet.payment.address);
    if (cached) setCreatorProfileState(cached);
    if (!authorization || accountBusy) {
      setProfileLoading(false);
      return;
    }
    let active = true;
    const revision = accountRevision.current;
    setProfileLoading(true);
    void Promise.all([fetchWalletAccount(authorization), fetchMyCreatorProfile(authorization)])
      .then(([nextAccount, profile]) => {
        if (!active || revision !== accountRevision.current) return;
        storeAccount(nextAccount);
        storeProfile(profile, nextAccount, wallet);
      })
      .catch(() => undefined)
      .finally(() => { if (active) setProfileLoading(false); });
    return () => { active = false; };
  }, [accountBusy, authorization, storeAccount, storeProfile, wallet]);

  useEffect(() => {
    let active = true;
    void fetchAppConfig()
      .then((nextConfig) => { if (active) setConfig(nextConfig); })
      .catch(() => { if (active) setError("Spam The Chain could not load."); })
      .finally(() => { if (active) setLoading(false); });
    void fetchRecommendedFees()
      .then((nextFees) => { if (active) setFees(nextFees); })
      .catch(() => { if (active) setFees(fallbackFees); });
    return () => { active = false; };
  }, []);

  const clearAccountError = useCallback(() => setAccountError(""), []);
  const value = useMemo<AppState>(() => ({
    config, fees, wallet, linkedWallets, account, setWallet, connectPrimaryWallet, switchWallet, reconnectWallet, linkWallet,
    removeLinkedWallet, makeMainWallet, accountBusy, accountError, clearAccountError, authorization, ordinalsVerified,
    authorizeWallet, disconnectWallet, creatorProfile, setCreatorProfile, profileLoading, walletModalOpen,
    setWalletModalOpen, walletModalMode, setWalletModalMode, completion, setCompletion, loading, error,
  }), [account, accountBusy, accountError, authorization, authorizeWallet, clearAccountError, completion, config, connectPrimaryWallet, creatorProfile, disconnectWallet, error, fees, linkWallet, linkedWallets, loading, makeMainWallet, ordinalsVerified, profileLoading, reconnectWallet, removeLinkedWallet, setCreatorProfile, setWallet, switchWallet, wallet, walletModalMode, walletModalOpen]);

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

// The provider and its hook intentionally share one small module.
// eslint-disable-next-line react-refresh/only-export-components
export function useAppState(): AppState {
  const value = useContext(AppStateContext);
  if (!value) throw new Error("useAppState must be used inside AppProvider");
  return value;
}
